import { inject, Injectable } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import Swal from 'sweetalert2';
import { LoaderService } from '../../loader/loader.service';
import { FormScore, IRiskFormAssignmentRequest } from '../scores-analysis/scores-analysis.models';
import { MultiSectionFormPreviewApiService } from './multi-section-form-preview-api.service';
import {
  IFormDataPayload,
  IOptioningData,
  IQuesField,
  ISectionInfo,
  ISegregatedSection,
} from './multi-section-form-preview.models';
import { BuyerPreviewService } from '../buyer-preview.service';

import { checkAndFormatDate } from '../../shared/date.utils';
import { environment } from '../../../environments/environment';
interface ICurrentlyBeingPreviewed {
  isEntireSectionBeingPreviewed: boolean | null;
  isIndividualSectionFieldBeingPreviewed: boolean | null;
  section: ISegregatedSection | null;
  fields: IQuesField[] | null;
}

@Injectable({
  providedIn: 'root',
})
export class MultiSectionFormPreviewService {
  public loaderService = inject(LoaderService);
  public buyerPreviewService = inject(BuyerPreviewService)

  public helper$: Subject<{ activeQuestionId: string; val: string }> =
    new Subject();

  private _cqScore$: Subject<FormScore[]> = new Subject();
  public get cqScore$(): Observable<FormScore[]> {
    return this._cqScore$.asObservable();
  }
  public set cqScore$(value: FormScore[]) {
    this._cqScore$.next(value);
  }

  private _scrollFieldIntoView$: Subject<IQuesField> = new Subject();
  public get scrollFieldIntoView$(): Observable<IQuesField> {
    return this._scrollFieldIntoView$.asObservable();
  }
  public set scrollFieldIntoView$(value: IQuesField) {
    this._scrollFieldIntoView$.next(value);
  }

  private _clearAnswerForField$: Subject<IQuesField> = new Subject();
  public get clearAnswerForField$(): Observable<IQuesField> {
    return this._clearAnswerForField$.asObservable();
  }
  public set clearAnswerForField$(value: IQuesField) {
    this._clearAnswerForField$.next(value);
  }

  toggleJsonStructure: boolean = false;
  private _toggleIndividualSectionFieldPreviewMode: boolean = Boolean(
    false,
  );
  public get toggleIndividualSectionFieldPreviewMode(): boolean {
    return this._toggleIndividualSectionFieldPreviewMode;
  }
  public set toggleIndividualSectionFieldPreviewMode(value: boolean) {
    this._toggleIndividualSectionFieldPreviewMode = value;
  }

  linkedFormDetails: any = {
    HasLinkedForms: false,
    LinkedBaseFormId: ''
  };
  public get linkedFormId(): number[] {
    return this.linkedFormDetails.LinkedBaseFormId;
  }
  public get hasLinkedForm(): boolean {
    return this.linkedFormDetails.HasLinkedForms;
  }


  allFields: IQuesField[];
  allSections: ISectionInfo[];
  activeSecInfo: ISectionInfo;
  activeSecSubSectionsInfo: ISectionInfo[];
  activeFieldInfo: IQuesField;
  isReviewClicked: boolean = false;

  isBuyerManagementPreview: boolean;
  mode: string;
  enableSubmitButton: boolean = false;
  enableReviewButton: boolean = false;

  private _isParentShared = false;
  public get isParentShared() {
    let localStorageIsParentShared = localStorage.getItem('IsParentShared');
    return (this._isParentShared =
      localStorageIsParentShared?.toLowerCase() === 'true' ? true : false);
  }

  private _formLayout = '';
  public get formLayout() {
    let localStorageFormLayout = localStorage.getItem('FormLayout');
    return (this._formLayout = localStorageFormLayout ?? '');
  }

  // SM-4061: Form name shown at the top of the Scores review modal
  private _formTitle = '';
  public get formTitle() {
    let localStorageFormTitle = localStorage.getItem('Title');
    return (this._formTitle = localStorageFormTitle ?? '');
  }

  private _formType = '';
  public get formType() {
    let localStorageFormType = localStorage.getItem('FormType');
    return (this._formType = localStorageFormType ?? '');
  }

  private _orgFormId = '';
  public get orgFormId() {
    let localStorageOrgFormId = localStorage.getItem('OrgFormId');
    return (this._orgFormId = localStorageOrgFormId ?? '');
  }

  private _submitFormRedirectURL = '';
  public get submitFormRedirectURL() {
    // Use environment's homePageUrl to build redirect URL for Supplier form details page
    // Format: {homePageUrl}/form-management/supplier-form-details/{OrgFormRefId}
    const homePageUrl = environment.homePageUrl?.replace(/\/$/, '') || '';
    if (homePageUrl && this.orgFormId) {
      return `${homePageUrl}/form-management/supplier-form-details/${this.orgFormId}`;
    }
    // Fallback to localStorage value if environment not configured
    let localStorageSubmitFormRedirectURL = localStorage.getItem(
      'SubmitFormRedirectURL',
    );
    return (this._submitFormRedirectURL =
      localStorageSubmitFormRedirectURL ?? '');
  }

  private _surveyId = '';
  public get surveyId() {
    let localStorageSurveyId = localStorage.getItem('SurveyId');
    return (this._surveyId = localStorageSurveyId ?? '');
  }

  showJumpToSectionBody: boolean = false;

  fieldsSegregatedIntoSections: {
    [key: string]: ISegregatedSection;
  } | null = null;

  getActiveSecFieldsSegregatedintoSections(): {
    [key: string]: ISegregatedSection;
  } {
    const res = Object.entries(this.fieldsSegregatedIntoSections)?.reduce(
      (acc, currVal) => {
        if (
          currVal[1]?.sectionDetails?.ParentID === this.activeSecInfo?.UniqueID
        ) {
          acc[currVal[0]] = currVal[1];
        }
        return acc;
      },
      {},
    );
    return res;
  }

  getSectionByFieldId(fieldId: string): ISectionInfo {
    const field = this.allFields.find((f) => f.data[0].QuestionId === fieldId);
    return this.getSection(field.sectionId);
  }

  getSection(sectionId: string): ISectionInfo {
    return this.getNestedSection(this.allSections, sectionId);
  }

  getActiveNestedSection() {
    let desiredResult: ISectionInfo;
    const currentPreviewValue: ISegregatedSection | any = Object.values(
      this.getCurrentPreview(),
    )[0];

    desiredResult = this.toggleIndividualSectionFieldPreviewMode
      ? this.getSection(this.getCurrentPreview().sectionId)
      : currentPreviewValue.sectionDetails;

    return desiredResult;
  }

  getBreadcrumb() {
    const sectionName = this.activeSecInfo?.Title;
    const subSectionName = this.getActiveNestedSection()?.Title;
    const breadCrumbs = [sectionName];
    if (subSectionName && sectionName !== subSectionName) {
      breadCrumbs.push(subSectionName);
    }
    return breadCrumbs;
  }

  // @ts-ignore
  getNestedSection(sectionData, secId): ISectionInfo {
    if (sectionData?.length) {
      for (let sec of sectionData) {
        if (sec.Sections?.length) {
          let desiredSec = this.getNestedSection(sec?.Sections, secId);
          if (desiredSec) return desiredSec;
        } else {
          if (sec?.UniqueID === secId) return sec;
        }
      }
    }
  }

  // @ts-ignore
  getProperActiveSecOrSubSec(): ISectionInfo {
    if (this.activeSecInfo && this.activeSecSubSectionsInfo?.length) {
      return (
        this.activeSecSubSectionsInfo.find(
          (section) => section.Sequence === 0,
        ) ??
        this.activeSecSubSectionsInfo.find((section) => section.Sequence === 1)
      );
    } else if (
      this.activeSecInfo &&
      !this.activeSecSubSectionsInfo?.length &&
      this.activeSecInfo?.Fields?.length
    ) {
      return this.activeSecInfo;
    }
  }

  getLastSubSectionFromActiveSection(): ISectionInfo {
    let desiredSection: ISectionInfo;
    if (this.activeSecInfo && this.activeSecSubSectionsInfo?.length) {
      desiredSection = this.activeSecSubSectionsInfo.at(-1);
    }
    return desiredSection;
  }
  getFirstSubSectionFromActiveSection(): ISectionInfo {
    let desiredSection: ISectionInfo;
    if (this.activeSecInfo && this.activeSecSubSectionsInfo?.length) {
      desiredSection = this.activeSecSubSectionsInfo.at(0);
    }
    return desiredSection;
  }

  // @ts-ignore
  getFirstActiveSecAndSubSec(): ISectionInfo {
    this.activeSecInfo = this.allSections[0];
    this.activeSecSubSectionsInfo = this.allSections[0].Sections;

    if (this.activeSecInfo && this.activeSecSubSectionsInfo?.length) {
      const subSec0 = this.activeSecSubSectionsInfo.find(
        (section) => section.Sequence === 0,
      );
      const subSec1 = this.activeSecSubSectionsInfo.find(
        (section) => section.Sequence === 1,
      );
      return (subSec0 ? subSec0 : subSec1) ?? this.activeSecSubSectionsInfo[0];
    } else if (
      this.activeSecInfo &&
      !this.activeSecSubSectionsInfo?.length &&
      this.activeSecInfo?.Fields?.length
    ) {
      return this.activeSecInfo;
    }
  }

  getFirstFieldFromActiveSecOrSubSec(): IQuesField {
    return (this.activeFieldInfo = this.getSegregatedSection(
      this.getProperActiveSecOrSubSec().UniqueID,
    ).fieldsList.at(0));
  }
  getLastFieldFromActiveSecOrSubSec(): IQuesField {
    const fieldsList = this.getSegregatedSection(
      this.getProperActiveSecOrSubSec().UniqueID,
    ).fieldsList;
    const lastFieldIdx = Number(fieldsList.length) - 1;
    return (this.activeFieldInfo = this.getProperLastFieldFromActiveSecOrSubSec(
      fieldsList,
      lastFieldIdx,
    ));
  }

  getProperLastFieldFromActiveSecOrSubSec(
    fieldsList: IQuesField[],
    idx: number,
  ) {
    const lastField = fieldsList.at(idx);
    if (lastField && lastField.isQnFieldVisibleOnPreviewArea) {
      return lastField;
    } else {
      return this.getProperLastFieldFromActiveSecOrSubSec(fieldsList, idx - 1);
    }
  }

  getSegregatedSection(sectionId: string): ISegregatedSection {
    return this.fieldsSegregatedIntoSections[sectionId];
  }

  // In the left side navigation panel - check whether a section or sub-section is collapsed or not
  // "Content" can be either sub-sections of questions(=fields=nodes)
  isSectionContentCollapsed(sectionId: string) {
    let desiredResult;
    const desiredSection =
      this.getSegregatedSection(sectionId)?.sectionDetails ??
      this.allSections?.find((sec) => sec?.UniqueID === sectionId);
    const subSections = desiredSection?.Sections?.length
      ? desiredSection?.Sections
      : [];
    if (subSections?.length) {
      desiredResult = subSections?.every(
        (subSec) =>
          this.fieldsSegregatedIntoSections?.[subSec?.UniqueID]
            ?.areSectionFieldsCollapsed ||
          this.fieldsSegregatedIntoSections?.[subSec?.UniqueID]
            ?.isParentSectionCollapsed,
      );
    } else {
      desiredResult =
        this.getSegregatedSection(sectionId)?.areSectionFieldsCollapsed;
    }
    return desiredResult;
  }

  // Check whether a section
  // has no subsections and
  // has only static fields
  isSectionStatic(sectionId: string) {
    const desiredSection =
      this.getSegregatedSection(sectionId)?.sectionDetails ??
      this.allSections?.find((sec) => sec?.UniqueID === sectionId);
    const subSections = desiredSection?.Sections?.length
      ? desiredSection?.Sections
      : [];
    const isSectionStatic =
      !Boolean(subSections.length) &&
      this.isEveryFieldInTheSectionStatic(desiredSection.UniqueID);
    return isSectionStatic;
  }

  isSectionOptional(sectionId) {
    const desiredSection =
      this.getSegregatedSection(sectionId)?.sectionDetails ??
      this.allSections?.find((sec) => sec?.UniqueID === sectionId);
    const subSections = desiredSection?.Sections?.length
      ? desiredSection?.Sections
      : [];
    const isSectionStatic =
      !Boolean(subSections.length) &&
      this.isEveryFieldInTheSectionOptional(desiredSection.UniqueID);
    return isSectionStatic;
  }

  // In the left side navigation panel - check whether a section or sub-section has all the field list items are invisible
  isEveryFieldInTheSectionOptional(sectionId: string) {
    const desiredSegregatedSection = this.getSegregatedSection(sectionId);
    const isEveryFieldInTheSectionOptional =
      desiredSegregatedSection?.fieldsList.every((f) => f.isQnFieldOptional);
    return isEveryFieldInTheSectionOptional;
  }

  // Check whether a section or sub-section has only the static fields
  isEveryFieldInTheSectionStatic(sectionId: string) {
    const desiredSegregatedSection = this.getSegregatedSection(sectionId);
    const isEveryFieldInTheSectionOptional =
      desiredSegregatedSection?.fieldsList.every((f) => this.isFieldStatic(f));
    return isEveryFieldInTheSectionOptional;
  }

  isFieldStatic(field: IQuesField) {
    return (
      !field.isQnFieldVisibleOnNavListItems &&
      field.isQnFieldVisibleOnPreviewArea
    );
  }

  areAllListItemsCollapsed() {
    return Object.entries(this.fieldsSegregatedIntoSections).every(
      ([secId, secContent]) => secContent.areSectionFieldsCollapsed,
    );
  }

  private _getCurrentPreviewUnformatted() {
    return this.fieldsSegregatedIntoSections
      ? Object.entries(this.fieldsSegregatedIntoSections).find(
          ([secId, secContent]) => {
            if (secContent.isEntireSectionBeingPreviewed) {
              return secContent;
            } else {
              if (secContent.isIndividualSectionFieldBeingPreviewed) {
                return secContent.fieldsList.find(
                  (field) => field.isFieldBeingPreviewedIndividually,
                );
              } else {
                return null;
              }
            }
          },
        )
      : null;
  }

  getCurrentPreview(): any {
    if (this._getCurrentPreviewUnformatted()?.length) {
      if (this.toggleIndividualSectionFieldPreviewMode) {
        let desiredResult: IQuesField;
        desiredResult = this.activeFieldInfo =
          this._getCurrentPreviewUnformatted()[1].fieldsList.find(
            (field) => field.isFieldBeingPreviewedIndividually,
          );
        if (!desiredResult) {
          if (this.getFirstActiveSecAndSubSec().DisplayType === 'One') {
            this.previewSection(this.getFirstActiveSecAndSubSec());
          } else {
            this.showEntireSectionPreview(
              this.getFirstActiveSecAndSubSec().UniqueID,
            );
          }
        } else {
          this.showEntireSectionPreview(desiredResult.sectionId);
        }
        return (desiredResult = this.activeFieldInfo);
      } else {
        return {
          [this._getCurrentPreviewUnformatted()[0]]:
            this._getCurrentPreviewUnformatted()[1],
        };
      }
    } else {
      return null;
    }
  }

  showEntireSectionPreview(sectionId: string) {
    Object.entries(this.fieldsSegregatedIntoSections).forEach(
      ([secId, secContent]) => {
        
        secContent.isIndividualSectionFieldBeingPreviewed = Boolean(
          secContent.sectionDetails.DisplayType === 'One',
        );
        if (secId === sectionId) {
          secContent.isEntireSectionBeingPreviewed = true;
          secContent.isSectionCurrentlyBeingPreviewed = true;
        } else {
          secContent.isEntireSectionBeingPreviewed = false;
          secContent.isSectionCurrentlyBeingPreviewed = false;
        }
      },
    );
  }
  hideEntireSectionPreview(sectionId: string) {
    this.fieldsSegregatedIntoSections[sectionId] = {
      ...this.getSegregatedSection(sectionId),
      isEntireSectionBeingPreviewed: false,
      isSectionCurrentlyBeingPreviewed: false,
      isIndividualSectionFieldBeingPreviewed: false,
    };
  }
  showBlankPreviewScreen() {
    Object.entries(this.fieldsSegregatedIntoSections).forEach(
      ([secId, secContent]) => {
        this.hideEntireSectionPreview(secId);
      },
    );
  }

  toggleFields(sectionId: string) {
    this.fieldsSegregatedIntoSections[sectionId] = {
      ...this.getSegregatedSection(sectionId),
      areSectionFieldsCollapsed:
        !this.getSegregatedSection(sectionId)?.areSectionFieldsCollapsed,
    };
  }

  toggleSubSections(sectionId: string) {
    this.fieldsSegregatedIntoSections[sectionId] = {
      ...this.getSegregatedSection(sectionId),
      isParentSectionCollapsed:
        !this.getSegregatedSection(sectionId)?.isParentSectionCollapsed,
    };
  }

  toggleChildrenContent(sectionId: string) {
    const desiredSection =
      this.getSegregatedSection(sectionId)?.sectionDetails ??
      this.allSections.find((sec) => sec.UniqueID === sectionId);
    const subSections = desiredSection.Sections?.length
      ? desiredSection.Sections
      : [];
    if (subSections.length) {
      subSections.forEach((subSec) => {
        this.fieldsSegregatedIntoSections[subSec.UniqueID] = {
          ...this.getSegregatedSection(subSec.UniqueID),
          areSectionFieldsCollapsed: !this.getSegregatedSection(subSec.UniqueID)
            ?.areSectionFieldsCollapsed,
          isParentSectionCollapsed: !this.getSegregatedSection(subSec.UniqueID)
            ?.isParentSectionCollapsed,
        };
      });
    } else {
      this.toggleFields(sectionId);
    }
  }

  showSectionFieldPreview(sectionField: IQuesField) {
    Object.entries(this.fieldsSegregatedIntoSections)?.forEach(
      ([secId, secContent]) => {
        secContent?.fieldsList?.forEach((field) => {
          if (
            field?.data[0]?.QuestionId === sectionField?.data[0]?.QuestionId
          ) {
            field.isFieldBeingPreviewedIndividually = true;
          } else {
            field.isFieldBeingPreviewedIndividually = false;
          }
        });
      },
    );
  }

  hideSectionFieldPreview(sectionField: IQuesField) {
    Object.entries(this.fieldsSegregatedIntoSections).forEach(
      ([secId, secContent]) => {
        secContent.fieldsList.forEach((field) => {
          if (field.data[0].QuestionId === sectionField.data[0].QuestionId) {
            field.isFieldBeingPreviewedIndividually = false;
          }
        });
      },
    );
  }

  doForceExpandAllSectionListItems = false;
  forceExpandAllSectionListItems() {
    Object.entries(this.fieldsSegregatedIntoSections).forEach(
      ([secId, secContent]) => {
        this.fieldsSegregatedIntoSections[secId].areSectionFieldsCollapsed =
          false;
        if (secContent.isSubSection) {
          this.fieldsSegregatedIntoSections[secId].isParentSectionCollapsed =
            false;
        }
      },
    );
    this.doForceExpandAllSectionListItems = true;
  }
  forceCollapseAllSectionListItems() {
    Object.entries(this.fieldsSegregatedIntoSections).forEach(
      ([secId, secContent]) => {
        this.fieldsSegregatedIntoSections[secId].areSectionFieldsCollapsed =
          true;
        if (secContent.isSubSection) {
          this.fieldsSegregatedIntoSections[secId].isParentSectionCollapsed =
            true;
        }
      },
    );
    this.doForceExpandAllSectionListItems = false;
  }

  expandAllSectionListItems() {
    this.expandAllSectionFields();
    this.expandAllSubSectionsOfSection();
  }

  expandAllSectionFields() {
    Object.entries(this.fieldsSegregatedIntoSections).forEach(
      ([secId, secContent]) => {
        this.fieldsSegregatedIntoSections[secId].areSectionFieldsCollapsed =
          false;
      },
    );
  }
  expandAllSubSectionsOfSection() {
    Object.entries(this.fieldsSegregatedIntoSections).forEach(
      ([secId, secContent]) => {
        this.fieldsSegregatedIntoSections[secId].isParentSectionCollapsed =
          false;
      },
    );
  }

  collapseAllSectionListItems() {
    this.collapseAllSectionFields();
    this.collapseAllSubSectionsOfSection();
  }

  collapseAllSectionFields() {
    Object.entries(this.fieldsSegregatedIntoSections).forEach(
      ([secId, secContent]) => {
        this.fieldsSegregatedIntoSections[secId].areSectionFieldsCollapsed =
          true;
      },
    );
  }
  collapseAllSubSectionsOfSection() {
    Object.entries(this.fieldsSegregatedIntoSections).forEach(
      ([secId, secContent]) => {
        this.fieldsSegregatedIntoSections[secId].isParentSectionCollapsed =
          true;
      },
    );
  }

  updateRequiredBooleanValues() {
    Object.entries(this.fieldsSegregatedIntoSections).forEach(
      ([secId, secContent]) => {
        secContent.isIndividualSectionFieldBeingPreviewed =
          this.toggleIndividualSectionFieldPreviewMode;
      },
    );
  }

  /**
   * Determine whether review actions should be enabled based on the presence
   * of a linked form for review submission.
   */
  shouldReviewLinkedForm(): boolean {
    return this.hasLinkedForm;
  }

  /**
   * Check if the base form has a linked form for review submission
   */
  checkHasLinkedForm(): void {
    const formId = localStorage.getItem('FormId');
    if (formId) {
      this.msfapi.hasLinkedForm(formId).subscribe({
        next: (result: any) => {
          this.linkedFormDetails = {
            ...result
          }
        },
        error: (err) => {
          console.error('Error checking linked form:', err);
          this.linkedFormDetails = {
            HasLinkedForms: false,
            LinkedBaseFormId: ''
          }
        }
      });
    }
  }

  /**
   * Check if CCA Score Preview button should be enabled
   * Shows when: CCA form type and user is on the last section (no more "next")
   */
  doEnableCCAScorePreviewButton(): boolean {
    return this.shouldReviewLinkedForm() && !this.getEnableNext();
  }

  doEnableGetScoresButton() {
    const isStandardForm = ['NIST', 'NISTScoreV1', 'CCRA', 'CQWG', 'CMMC'].includes(
      this.formType,
    );
    return isStandardForm && !this.getEnableNext();
  }

  getEnablePrevious() {
    if (this.getCurrentPreview()) {
      if (this.toggleIndividualSectionFieldPreviewMode) {
        return (
          Boolean(this.getPreviousField()) ||
          Boolean(this.getPreviousSubSec()) ||
          Boolean(this.getPreviousSec())
        );
      } else {
        return (
          Boolean(this.getPreviousSubSec()) || Boolean(this.getPreviousSec())
        );
      }
    } else return false;
  }
  getEnableNext() {
    if (this.getCurrentPreview()) {
      if (this.toggleIndividualSectionFieldPreviewMode) {
        return (
          Boolean(this.getNextField()) ||
          Boolean(this.getNextSubSec()) ||
          Boolean(this.getNextSec())
        );
      } else {
        return Boolean(this.getNextSubSec()) || Boolean(this.getNextSec());
      }
    } else return false;
  }

  getDisableNext() {
    return (
      this.activeFieldInfo &&
      this.activeFieldInfo.isFieldBeingPreviewedIndividually &&
      this.activeFieldInfo.isQnFieldVisibleOnNavListItems &&
      !this.activeFieldInfo.answered
    );
  }

  getPreviousSec(): ISectionInfo {
    const activeSecIdx = this.allSections?.findIndex(
      (sec) => sec.UniqueID === this.activeSecInfo.UniqueID,
    );
    const previousSec = this.allSections?.find(
      (sec, idx) => idx === Number(activeSecIdx) - 1,
    );
    return previousSec;
  }
  getNextSec(): ISectionInfo {
    const activeSecIdx = this.allSections?.findIndex(
      (sec) => sec.UniqueID === this.activeSecInfo.UniqueID,
    );
    const nextSec = this.allSections?.find(
      (sec, idx) => idx === Number(activeSecIdx) + 1,
    );
    return nextSec;
  }

  getActivelyPreviewedSegSec() {
    const activelyPreviewedSegSec = Object.entries(
      this.fieldsSegregatedIntoSections,
    ).find(
      ([secId, secContent]) => secContent.isSectionCurrentlyBeingPreviewed,
    );
    if (activelyPreviewedSegSec?.length) {
      const result = {
        [activelyPreviewedSegSec[0]]: activelyPreviewedSegSec[1],
      };
      return result;
    } else {
      return null;
    }
  }

  getPreviousSubSec(): ISectionInfo {
    const desiredSubSecId = this.toggleIndividualSectionFieldPreviewMode
      ? this.getCurrentPreview()?.sectionId
      : Object.keys(this.getCurrentPreview())[0];
    const activeSubSecIdx = this.activeSecSubSectionsInfo?.findIndex(
      (sec) => sec.UniqueID === desiredSubSecId,
    );
    const previousSubSec = this.activeSecSubSectionsInfo?.find(
      (sec, idx) => idx === Number(activeSubSecIdx) - 1,
    );
    return previousSubSec;
  }
  getNextSubSec(): ISectionInfo {
    const desiredSubSecId = this.toggleIndividualSectionFieldPreviewMode
      ? this.getCurrentPreview()?.sectionId
      : Object.keys(this.getCurrentPreview())[0];
    const activeSubSecIdx = this.activeSecSubSectionsInfo?.findIndex(
      (sec) => sec.UniqueID === desiredSubSecId,
    );
    const nextSubSec = this.activeSecSubSectionsInfo?.find(
      (sec, idx) => idx === Number(activeSubSecIdx) + 1,
    );
    return nextSubSec;
  }

  getPreviousField() {
    const activeSecOrSubSecId = this.getActivelyPreviewedSegSec()
      ? Object.keys(this.getActivelyPreviewedSegSec())?.[0]
      : '';
    const activeFieldId = this.getCurrentPreview()?.data?.[0]?.QuestionId;
    const activeFieldIdx = this.fieldsSegregatedIntoSections[
      activeSecOrSubSecId
    ]?.fieldsList?.findIndex((f) => f?.data?.[0].QuestionId === activeFieldId);
    const previousField = this.getProperPreviousField(
      activeSecOrSubSecId,
      activeFieldIdx,
    );
    if (previousField?.data?.[0]?.QuestionId === activeFieldId) return null;
    return previousField;
  }

  getProperPreviousField(
    activeSecOrSubSecId: string,
    activeFieldIdx: number,
  ): IQuesField {
    let desiredResult;
    let previousField =
      this.fieldsSegregatedIntoSections?.[activeSecOrSubSecId]?.fieldsList[
        Number(activeFieldIdx) - 1
      ];
    if (previousField && previousField.isQnFieldVisibleOnPreviewArea) {
      desiredResult = previousField;
    } else if (previousField && !previousField.isQnFieldVisibleOnPreviewArea) {
      desiredResult = this.getProperPreviousField(
        activeSecOrSubSecId,
        activeFieldIdx - 1,
      );
    }
    return desiredResult;
  }

  getProperNextField(
    activeSecOrSubSecId: string,
    activeFieldIdx: number,
  ): IQuesField {
    let desiredResult;
    let nextField =
      this.fieldsSegregatedIntoSections?.[activeSecOrSubSecId]?.fieldsList[
        Number(activeFieldIdx) + 1
      ];
    if (nextField && nextField.isQnFieldVisibleOnPreviewArea) {
      desiredResult = nextField;
    } else if (nextField && !nextField.isQnFieldVisibleOnPreviewArea) {
      desiredResult = this.getProperNextField(
        activeSecOrSubSecId,
        activeFieldIdx + 1,
      );
    }
    return desiredResult;
  }

  getNextField() {
    const activeSecOrSubSecId = this.getActivelyPreviewedSegSec()
      ? Object.keys(this.getActivelyPreviewedSegSec())?.[0]
      : '';
    const activeFieldId = this.getCurrentPreview()?.data?.[0]?.QuestionId;
    const activeFieldIdx = this.fieldsSegregatedIntoSections[
      activeSecOrSubSecId
    ]?.fieldsList?.findIndex((f) => f?.data?.[0].QuestionId === activeFieldId);
    const nextField = this.getProperNextField(
      activeSecOrSubSecId,
      activeFieldIdx,
    );
    if (nextField?.data?.[0]?.QuestionId === activeFieldId) return null;
    return nextField;
  }

  goToNext() {
    if (this.toggleIndividualSectionFieldPreviewMode) {
      if (this.getNextField()) {
        this.goToNextField();
      } else if (this.getNextSubSec()) {
        this.goToNextSubSec();
      } else {
        this.goToNextSec();
      }
    } else {
      if (this.getNextSubSec()) {
        this.goToNextSubSec();
      } else {
        this.goToNextSec();
      }
    }
  }
  goToPrevious() {
    if (this.toggleIndividualSectionFieldPreviewMode) {
      if (this.getPreviousField()) {
        this.goToPreviousField();
      } else if (this.getPreviousSubSec()) {
        this.goToPreviousSubSec();
      } else {
        this.goToPreviousSec();
      }
    } else {
      if (this.getPreviousSubSec()) {
        this.goToPreviousSubSec();
      } else {
        this.goToPreviousSec();
      }
    }
  }

  goToPreviousSec() {
    this.activeSecInfo = this.getPreviousSec() ?? this.allSections?.[0];
    this.toggleIndividualSectionFieldPreviewMode =
      this.activeSecInfo.DisplayType === 'One';
    this.previewLastSubSec();
  }
  goToNextSec() {
    this.activeSecInfo = this.getNextSec() ?? this.allSections?.[0];
    this.toggleIndividualSectionFieldPreviewMode =
      this.activeSecInfo.DisplayType === 'One';
    this.previewFirstSubSec();
  }
  goToPreviousSubSec() {
    this.showEntireSectionPreview(this.getPreviousSubSec().UniqueID);
    if (this.toggleIndividualSectionFieldPreviewMode) {

      const activeSubSec = this.activeSecSubSectionsInfo.find(
        (sec) =>
          sec.UniqueID === Object.keys(this.getActivelyPreviewedSegSec())[0],
      );
      this.previewLastFieldOf(activeSubSec);
    }
  }
  goToNextSubSec() {
    this.showEntireSectionPreview(this.getNextSubSec().UniqueID);
    if (this.toggleIndividualSectionFieldPreviewMode) {

      const activeSubSec = this.activeSecSubSectionsInfo.find(
        (sec) =>
          sec.UniqueID === Object.keys(this.getActivelyPreviewedSegSec())[0],
      );
      this.previewFirstFieldOf(activeSubSec);
    }
  }
  goToPreviousField() {
    this.showSectionFieldPreview(this.getPreviousField());
  }
  goToNextField() {
    this.showSectionFieldPreview(this.getNextField());
  }

  previewFirstFieldOf(secOrSubSec: ISectionInfo) {
    const desiredField = secOrSubSec.Fields.at(0);
    const firstFieldOfActiveSecOrSubSec = this.allFields.find(
      (f) => f.data[0].QuestionId === desiredField.UniqueID,
    );
    this.showSectionFieldPreview(firstFieldOfActiveSecOrSubSec);
  }
  previewFirstField() {
    const desiredField = this.activeSecSubSectionsInfo?.length
      ? this.activeSecSubSectionsInfo?.at(0)?.Fields?.at(0)
      : this.activeSecInfo?.Fields?.at(0);
    const firstFieldOfActiveSecOrSubSec = this.allFields.find(
      (f) => f.data[0].QuestionId === desiredField.UniqueID,
    );
    this.showSectionFieldPreview(firstFieldOfActiveSecOrSubSec);
  }
  previewLastFieldOf(secOrSubSec: ISectionInfo) {
    const desiredField = secOrSubSec.Fields.at(-1);
    const firstFieldOfActiveSecOrSubSec = this.allFields.find(
      (f) => f.data[0].QuestionId === desiredField.UniqueID,
    );
    this.showSectionFieldPreview(firstFieldOfActiveSecOrSubSec);
  }
  previewFirstSubSec() {
    this.activeSecSubSectionsInfo = this.activeSecInfo?.Sections;
    if (this.getFirstSubSectionFromActiveSection()) {
      this.showEntireSectionPreview(
        this.getFirstSubSectionFromActiveSection()?.UniqueID,
      );
    } else if (this.getFirstFieldFromActiveSecOrSubSec()) {
      this.showEntireSectionPreview(
        this.getFirstFieldFromActiveSecOrSubSec().sectionId,
      );
      this.showSectionFieldPreview(this.getFirstFieldFromActiveSecOrSubSec());
    }
    if (this.toggleIndividualSectionFieldPreviewMode) {
      this.previewFirstField();
    }
  }
  previewLastSubSec() {
    this.activeSecSubSectionsInfo = this.activeSecInfo?.Sections;
    if (this.getLastSubSectionFromActiveSection()) {
      this.showEntireSectionPreview(
        this.getLastSubSectionFromActiveSection().UniqueID,
      );
    } else if (this.getLastFieldFromActiveSecOrSubSec()) {
      this.showEntireSectionPreview(
        this.getLastFieldFromActiveSecOrSubSec().sectionId,
      );
      this.showSectionFieldPreview(this.getLastFieldFromActiveSecOrSubSec());
    }
  }
  previewSection(section: ISectionInfo) {
    this.activeSecInfo = this.allSections?.find(
      (sec) => sec?.UniqueID === section?.UniqueID,
    );
    this.previewFirstSubSec();
    this.toggleIndividualSectionFieldPreviewMode =
      section.DisplayType === 'One';
  }

  public msfapi = inject(MultiSectionFormPreviewApiService);

  // SM-1704: Store pending risk form assignment to be processed after CCA submission
  public pendingRiskFormAssignment: IRiskFormAssignmentRequest | null = null;

  swalWithBootstrapButtons = Swal.mixin({
    customClass: {
      confirmButton: 'btn',
    },
    buttonsStyling: false,
  });

  getSelectedAnswers(field: IQuesField): string[] {
    let selectedAnswers: string[];

    // SM-3610: For dropdown/checkbox/multiselect-dropdown/radio fields, always extract answer from IOptioningData
    // regardless of whether field is disabled. This ensures locked fields (from default answer rules) 
    // still have their answers properly extracted for save.
    if (
      ['dropdown', 'checkbox', 'multiselect-dropdown', 'radio'].includes(
        field.data[1]?.type?.toLowerCase(),
      )
    ) {
      selectedAnswers = (
        field?.nodeLogic?.selectedAnswers as IOptioningData[]
      )?.map((x) => x.answer);
    } else {
      selectedAnswers = field?.nodeLogic?.selectedAnswers as string[];
    }
    return selectedAnswers;
  }

  getScores(isReview: boolean = false) {
    this.isReviewClicked = isReview;
    const formData: IFormDataPayload[] = [...this.allFields]
      // SM-3610: Include conditionally visible fields (isQnFieldVisibleOnPreviewArea) even if isHidden is true in config
      .filter((field) => !this.doesHiddenInputExistFor(field) || field.isQnFieldVisibleOnPreviewArea)
      .map((field) => {
        let selectedAnswers: string[] = this.getSelectedAnswers(field);

        const questionIdentifier = field?.data[0]?.QuestionId;

        return {
          question: questionIdentifier ?? '',
          answers: selectedAnswers ?? [''],
        };
      });

    // Send as single object with formData and formType properties
    // Backend expects ScoresByJson object, not an array
    // Include surveyId for CMMC forms to get proper CMMC scores with Risk section
    const getScoresForThePayload = {
      formData: formData,
      formType: this.formType,
      surveyId: this.surveyId,
    };
    this.msfapi.getCQScoresByJSON(getScoresForThePayload).subscribe({
      next: (resp) => {
        if (resp.success) {
          this.cqScore$ = resp.data[0];
        } else {
          this.swalWithBootstrapButtons.fire({
            title: 'Failure!',
            text:
              !resp.success || resp.msg != ''
                ? resp.msg
                : 'Failed to fetch scores.',
            icon: 'error',
            confirmButtonText: 'OK',
          });
        }
      },
      error: (err) => {
        this.swalWithBootstrapButtons.fire({
          title: 'Failure!',
          text:
            !err.success || err.msg != '' ? err.msg : 'Failed to fetch scores.',
          icon: 'error',
          confirmButtonText: 'OK',
        });
      },
    });
  }

  modalHelper$: Subject<string> = new Subject();
  submitConfirm() {
    this.modalHelper$.next('SUBMIT_CONFIRMATION_MODAL_OPEN');
  }
  submit() {
    // SM-1704: Submit CMMC form first, then assign risk form only on success
    const userAnswers = {
      formId: this.orgFormId,
      SurveyUniqueID: this.surveyId,
      OrgFormUniqueId: this.orgFormId,
    };

    this.msfapi.submitUserAnswers(userAnswers).subscribe({
      next: (resp) => {
        if (resp.IsSuccess) {
          // CMMC submission successful - now process pending risk form assignment if any
          if (this.pendingRiskFormAssignment) {
            this.msfapi.assignRiskForm(this.pendingRiskFormAssignment).subscribe({
              next: (assignResponse) => {
                this.pendingRiskFormAssignment = null;
                
                if (assignResponse.Success) {
                  // Both succeeded
                  this.showSuccessAndRedirect('Form submitted successfully');
                } else {
                  // CMMC succeeded but assignment failed - show warning but still redirect
                  this.swalWithBootstrapButtons.fire({
                    title: 'Partial Success',
                    text: 'Form submitted successfully, but risk form assignment failed: ' + 
                          (assignResponse.Message || 'Unknown error'),
                    icon: 'warning',
                    confirmButtonText: 'OK'
                  }).then(() => {
                    this.redirectAfterSubmit();
                  });
                }
              },
              error: (err) => {
                this.pendingRiskFormAssignment = null;
                // CMMC succeeded but assignment failed - show warning but still redirect
                this.swalWithBootstrapButtons.fire({
                  title: 'Partial Success',
                  text: 'Form submitted successfully, but risk form assignment encountered an error.',
                  icon: 'warning',
                  confirmButtonText: 'OK'
                }).then(() => {
                  this.redirectAfterSubmit();
                });
              }
            });
          } else {
            // No pending assignment, just show success
            this.showSuccessAndRedirect(resp.Message || 'Form submitted successfully');
          }
        } else {
          // CMMC submission failed - don't assign risk form, clear pending
          this.pendingRiskFormAssignment = null;
          this.swalWithBootstrapButtons.fire({
            title: 'Failure!',
            text: resp.Message || 'Failed to submit the form.',
            icon: 'error',
            confirmButtonText: 'OK',
          });
        }
      },
      error: (err) => {
        // CMMC submission failed - don't assign risk form, clear pending
        this.pendingRiskFormAssignment = null;
        this.swalWithBootstrapButtons.fire({
          title: 'Failure!',
          text: !err.Success || err.Msg != '' ? err.Msg : 'Failed to submit the form.',
          icon: 'error',
          confirmButtonText: 'OK',
        });
        console.log("Multi section form submit failed");
      },
    });
  }

  // SM-1704: Helper to show success message and redirect
  private showSuccessAndRedirect(message: string) {
    this.swalWithBootstrapButtons.fire({
      title: 'Success!',
      text: message,
      icon: 'success',
      confirmButtonText: 'OK',
    }).then((result) => {
      if (result.isConfirmed) {
        this.redirectAfterSubmit();
      }
      console.log("Multi section form submit successful");
    });
  }

  // SM-1704: Helper to redirect after submit
  private redirectAfterSubmit() {
    const redirectUrl = this.submitFormRedirectURL;
    window.parent.location.href = redirectUrl;
    this.loaderService.showLoader();
  }
  /**
   * SM-3009: Angular UI base URL from environment.homePageUrl (e.g.
   * https://ui-sm.exostardev.com/obmui). Trailing slashes are trimmed so the
   * route can be appended directly.
   */
  private getAngularBaseUrl(): string {
    return (environment.homePageUrl || '').replace(/\/+$/, '');
  }

  /**
   * SM-3009: Route in the Angular app that the preview's top-right "X" button
   * navigates back to, based on the preview context:
   *  - supplierEdit (Submit visible)               -> supplier form list
   *  - previewDisabled (pre-populated, no Submit)   -> form lifecycle
   *  - buyer management preview (editable, no Submit) -> that form's details
   */
  public get closePreviewRoute(): string {
    if (this.mode === 'supplierEdit') {
      return '/form-management';
    }
    if (this.mode === 'previewDisabled') {
      return '/form-management/form-lifecycle';
    }
    // SM-3367: Use OrgFormId (UUID) instead of FormId (numeric) - the API expects the org form unique ID
    return this.orgFormId ? `/form-management/form-details/${this.orgFormId}` : '/form-management/form-lifecycle';
  }

  /**
   * SM-3009: Navigate back to the Angular application at the route that matches
   * the current preview context.
   */
  closePreview() {
    this.loaderService.showLoader();
    window.parent.location.href = `${this.getAngularBaseUrl()}${this.closePreviewRoute}`;
  }

  exit() {
    if (
      window.confirm('Any unsaved data will be lost. Do you want to continue?')
    ) {
      this.loaderService.showLoader();
      window.parent.location.href = this.submitFormRedirectURL;
    }
  }

  /**
   * SM-1587: Exit without confirmation for read-only (parent shared) forms.
   * No data can be modified, so no unsaved data warning is needed.
   */
  exitReadOnly() {
    this.loaderService.showLoader();
    window.parent.location.href = this.submitFormRedirectURL;
  }

  /**
   * SM-4207: Check if a field is a Submitter-type field.
   * In multi-section forms, Submitter fields are identified by their section title.
   * Submitter fields (Vendor Name, POC, DUNS, CAGE, etc.) only require `answered` check, not `valid`.
   */
  isFieldSubmitter(field: IQuesField): boolean {
    // For multi-section forms, check if the field belongs to a Submitter section
    return this.getSection(field.sectionId)?.Title === 'Submitter' ||
           field?.displayquestion === 'Submitter' ||
           field?.data?.[0]?.TypeValue === 'Submitter' ||
           field?.data?.[0]?.type?.toLowerCase() === 'submitter';
  }

  /**
   * SM-4207: Check if all non-Submitter fields are answered and valid.
   * Submitter fields are excluded because they don't have validation rules - only need to be answered.
   */
  areAllNonSubmitterFieldsAnsweredAndValid(): boolean {
    return [...this.allFields]
      ?.filter(
        (field) =>
          // SM-3610: Include conditionally visible fields (isQnFieldVisibleOnPreviewArea) even if isHidden is true in config
          (!this.doesHiddenInputExistFor(field) || field.isQnFieldVisibleOnPreviewArea) &&
          !this.doesDisableInputExistFor(field) &&
          field.isQnFieldVisibleOnPreviewArea &&
          !field.isQnFieldOptional &&
          !this.isFieldSubmitter(field), // SM-4207: Exclude Submitter fields
      )
      ?.every((field) => field.answered && field.valid);
  }

  /**
   * SM-4207: Check if all Submitter fields are answered.
   * Submitter fields only need to be answered - they don't have `valid` set correctly.
   */
  areAllSubmitterFieldsAnswered(): boolean {
    return [...this.allFields]
      ?.filter(
        (field) =>
          this.isFieldSubmitter(field) &&
          field.isQnFieldVisibleOnPreviewArea &&
          !field.isQnFieldOptional,
      )
      ?.every((field) => field.answered);
  }

  /**
   * SM-4207: Combined check for Submit button enablement.
   * Non-Submitter fields must be answered AND valid.
   * Submitter fields only need to be answered (they don't have validation).
   */
  areAllFieldsAnsweredAndValid() {
    return this.areAllNonSubmitterFieldsAnsweredAndValid() && this.areAllSubmitterFieldsAnswered();
  }

  save(triggeredFrom?: string) {
    // SM-1587: Block save operations for parent-shared (read-only) forms
    if (this.isParentShared) {
      this.swalWithBootstrapButtons.fire({
        title: 'Read-only form',
        text: 'This form is read-only and cannot be saved.',
        icon: 'info',
        confirmButtonText: 'OK',
      });
      return;
    }

    const userAnswers = {
      id: this.orgFormId,
      jsonResponse: [...this.allFields]
        // SM-3610: Include conditionally visible fields (isQnFieldVisibleOnPreviewArea) even if isHidden is true in config
        ?.filter((field) => !this.doesHiddenInputExistFor(field) || field.isQnFieldVisibleOnPreviewArea)
        ?.map((field) => {
          let answers = this.getSelectedAnswers(field);

          // Convert date fields to YYYY-MM-DD
          if (field?.data[1]?.type?.toLowerCase() === 'date' && Array.isArray(answers)) {
            answers = answers.map(ans => {
              const formatted = checkAndFormatDate(ans);
              return formatted ?? ans;
            });
          }

          return {
            id: field?.data[0]?.QuestionId,
            question: field?.data[0]?.dispalyFCText ?? '',
            answers: answers?.join('|')?.toString() ?? '',
          };
        }),
      FormLayout: this.formLayout,
    };

    this.msfapi.saveUserAnswers(userAnswers).subscribe({
      next: (resp) => {
        this.enableSubmitButton =
          resp.IsSuccess && this.areAllFieldsAnsweredAndValid();
        this.enableReviewButton = this.enableSubmitButton;
        if (!triggeredFrom) {
          if (resp.IsSuccess) {
            // SM-1945: Updated save confirmation modal to clarify draft vs submission
            this.swalWithBootstrapButtons.fire({
              title: 'Responses saved',
              html: `
                <div class="alert alert-info mb-3" role="alert" style="font-size: 0.85rem; display: flex; align-items: flex-start; text-align: left;">
                  <i class="fa fa-info-circle" style="margin-right: 8px; margin-top: 3px; flex-shrink: 0;"></i>
                  <span><strong>This is not a submission.</strong> Your responses have been saved as a draft. The form has not been submitted to your Prime.</span>
                </div>
                <p class="text-start mb-2" style="font-size: 0.85rem;">To submit this form, please navigate to the last question in the final section and click the <strong>Review</strong> or <strong>Submit</strong> button.</p>
                <p class="text-start text-muted mb-0" style="font-size: 0.8rem;">Your saved responses will be retained if you exit and return later.</p>
              `,
              icon: 'success',
              confirmButtonText: 'OK',
            });
          } else {
            this.swalWithBootstrapButtons.fire({
              title: 'Failure!',
              text: resp.Message || 'Failed to save the form.',
              icon: 'error',
              confirmButtonText: 'OK',
            });
          }
        }
      },
      error: (err) => {
        if (!triggeredFrom) {
          this.swalWithBootstrapButtons.fire({
            title: 'Failure!',
            text:
              !err.Success || err.Msg != ''
                ? err.Msg
                : 'Failed to save the form.',
            icon: 'error',
            confirmButtonText: 'OK',
          });
        }
      },
    });
  }

  doesHiddenInputExistFor(field: IQuesField) {
    return field?.data?.some((fieldItem) => fieldItem?.isHidden);
  }

  getSupplierUsers() {
    return this.msfapi.getSupplierUsers();
  }

  //Disabled inputs
  doesDisableInputExistFor(field: IQuesField) {
    return field?.data?.some((fieldItem) => fieldItem?.isDisableInputChecked);
  }

  getAppropriateField(field) {
    return this.doesDisableInputExistFor(field)
      ? this.updateDisabledFieldOptioningData(field)
      : field;
  }

  updateDisabledFieldOptioningData(field: IQuesField) {
    if (field?.data[1]?.type?.toLowerCase() === 'dropdown') {
      field.data[1].dropdowndata[0].selected = true;
    }
    if (field?.data[1]?.type?.toLowerCase() === 'radio') {
      field.data[1].radiodata[0].selected = true;
    }
    if (field?.data[1]?.type?.toLowerCase() === 'checkbox') {
      field.data[1].checkboxdata[0].selected = true;
    }
    return field;
  }

  // @ts-ignore
  getDisabledFieldAnswer(field: IQuesField) {
    if (field?.data[1]?.type?.toLowerCase() === 'dropdown') {
      return [field?.data[1]?.dropdowndata?.at(0)?.answer ?? ''];
    } else if (field?.data[1]?.type?.toLowerCase() === 'radio') {
      return [field?.data[1]?.radiodata?.at(0)?.answer ?? ''];
    } else if (field?.data[1]?.type?.toLowerCase() === 'checkbox') {
      return [field?.data[1]?.checkboxdata?.at(0)?.answer ?? ''];
    }
  }
}
